import { Camera, Lock } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  type AffiliateRecord,
  submitApplication,
  updateAffiliate,
} from "@/lib/affiliate";
import { SOCIAL_PLATFORMS } from "@/lib/social-utils";
import { useAffiliateForm, buildSocialsPayload } from "@/hooks/useAffiliateForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  affiliate?: AffiliateRecord;
  email?: string;
  onApplied?: (record: AffiliateRecord) => void;
  onUpdated?: (record: AffiliateRecord) => void;
}

export function AffiliateRegistrationForm({ affiliate, email, onApplied, onUpdated }: Props) {
  const isEdit = !!affiliate;
  const form = useAffiliateForm(affiliate);
  const accountEmail = affiliate?.email ?? email;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.setError("");
    form.setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        photoUrl: form.photoUrl || undefined,
        phone: form.phone || undefined,
        secondaryEmail: form.secondaryEmail || undefined,
        socials: buildSocialsPayload(form.socialState),
      };

      if (isEdit) {
        const record = await updateAffiliate(payload);
        onUpdated?.(record);
      } else {
        const record = await submitApplication(payload);
        onApplied?.(record);
      }
    } catch (err) {
      const fallback = isEdit ? "Failed to save changes" : "Failed to submit application";
      form.setError(err instanceof Error ? err.message : fallback);
      form.setSubmitting(false);
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit Profile" : "Affiliate Program"}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? "Update your affiliate profile information."
              : "Join our affiliate program and earn commissions by referring new users to n8nvibes."}
          </p>
        </CardHeader>
        <CardContent>
          {form.error && (
            <div className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {form.error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* Photo upload */}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => form.fileRef.current?.click()}
                className="relative h-20 w-20 rounded-full bg-muted flex items-center justify-center overflow-hidden border border-border hover:border-foreground/30 transition-colors"
              >
                {form.photoPreview ? (
                  <img
                    src={form.photoPreview}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Camera className="h-6 w-6 text-muted-foreground" />
                )}
                {form.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-foreground" />
                  </div>
                )}
              </button>
              <span className="text-xs text-muted-foreground">
                {form.uploading ? "Uploading..." : "Upload photo (optional)"}
              </span>
              <input
                ref={form.fileRef}
                type="file"
                accept="image/*"
                onChange={form.handlePhotoSelect}
                className="hidden"
              />
            </div>

            {/* Privacy notice */}
            <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              Your phone and email are for internal contact only and will not be shared publicly. Your name, photo, and social media links are public.
            </div>

            {/* Account email (readonly) */}
            {accountEmail && (
              <div className="flex flex-col gap-2">
                <Label>Account Email</Label>
                <div className="relative">
                  <Input
                    value={accountEmail}
                    readOnly
                    className="pr-9 bg-muted/30 text-muted-foreground cursor-default"
                  />
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                </div>
              </div>
            )}

            {/* Name */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="aff-name">Name *</Label>
              <Input
                id="aff-name"
                value={form.name}
                onChange={(e) => form.setName(e.target.value)}
                placeholder="Your full name"
                required
              />
            </div>

            {/* WhatsApp */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="aff-phone">WhatsApp</Label>
              <PhoneInput
                id="aff-phone"
                value={form.phone}
                onChange={(v) => form.setPhone(v ?? "")}
                defaultCountry="BR"
                international
                className="phone-input-wrapper"
              />
            </div>

            {/* Secondary Email */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="aff-secondary-email">Secondary Email</Label>
              <Input
                id="aff-secondary-email"
                type="email"
                value={form.secondaryEmail}
                onChange={(e) => form.setSecondaryEmail(e.target.value)}
                placeholder="alternate@example.com"
              />
            </div>

            {/* Social media */}
            <div className="flex flex-col gap-3">
              <Label className="text-sm font-medium">
                Social Media (optional)
              </Label>
              <div className="flex flex-col gap-3">
                {SOCIAL_PLATFORMS.map((platform) => {
                  const state = form.socialState[platform.key];
                  const Icon = platform.icon;
                  return (
                    <div key={platform.key} className="flex flex-col gap-1">
                      <Label htmlFor={`aff-${platform.key}`} className="text-xs text-muted-foreground">
                        {platform.label}
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          <Icon className="size-4" />
                        </span>
                        <Input
                          id={`aff-${platform.key}`}
                          value={state.value}
                          onChange={(e) => state.set(e.target.value)}
                          placeholder={platform.placeholder}
                          className="pl-9"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={form.submitting || form.uploading}
            >
              {form.submitting
                ? isEdit ? "Saving..." : "Submitting..."
                : isEdit ? "Save Changes" : "Apply"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
